import { ButtonGroup, HStack, modals, VStack, Box, IconButton, Input, Text } from '@cad-arc';
import { formatMonthDayYearHoursMinutesSeconds } from '../common/helpers/utils';
import { UseMutationResult } from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { ShareEventSearchForm } from '@mark43/cad-api';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { useForm } from 'react-hook-form';
import { FormDateInput } from '../forms/integrated-form-controls/form-date-input';

export const SHARE_SEARCH_BUTTON_ARIA = 'Share saved search';
export const GENERATE_LOG_BUTTON_ARIA = 'Generate log';
const SAVED_SEARCH_NAME_MAX_LENGTH = 128;

interface SavedSearchProps {
    savedSearchId: number;
    savedSearchName: string;
    lastExecutedDateUtc: string;
    formattedParams: string;
    runSavedSearch: (dateOverride?: SavedSearchDateRangeForm) => void;
    updateSavedSearchNameMutation: UseMutationResult<
        any,
        unknown,
        {
            savedSearchId: number;
            newName: string;
        },
        unknown
    >;
    deleteSavedSearch: () => Promise<unknown>;
    refetchSavedSearches: () => void;
    shareEventSearchForm?: ShareEventSearchForm;
    onGenerateLog?: (dateOverride?: SavedSearchDateRangeForm) => void;
    showDateRangeOverride?: boolean;
}

export type SavedSearchDateRangeForm = { startDate: string; endDate: string };

export function SavedSearch({
    savedSearchId,
    savedSearchName,
    lastExecutedDateUtc,
    formattedParams,
    runSavedSearch,
    updateSavedSearchNameMutation,
    deleteSavedSearch,
    refetchSavedSearches,
    shareEventSearchForm,
    onGenerateLog,
    showDateRangeOverride,
}: SavedSearchProps) {
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [newName, setNewName] = useState<string>(savedSearchName);
    const newNameIsValid = newName.length > 0 && newName.length <= SAVED_SEARCH_NAME_MAX_LENGTH;
    const { manageDepartmentSharedEventSearchesEnabled } = useEnabledFeatures();

    const validateForm = () => {
        const { startDate, endDate } = dateRangeForm.getValues();

        if ((!startDate && !endDate) || (startDate && endDate)) {
            return true;
        }

        return 'Start and end are required.';
    };

    const dateRangeForm = useForm<SavedSearchDateRangeForm>();

    const { open } = useFullscreenModalControls();
    const updateSavedSearchName = useCallback(
        (savedSearchId: number, newName: string) => {
            return updateSavedSearchNameMutation
                .mutateAsync({
                    savedSearchId,
                    newName,
                })
                .then(() => {
                    return refetchSavedSearches();
                })
                .then(() => {
                    setIsEditMode(false);
                    setNewName(newName);
                });
        },
        [updateSavedSearchNameMutation, refetchSavedSearches]
    );
    const cancelEdit = useCallback(() => {
        setIsEditMode(false);
        setNewName(savedSearchName);
    }, [savedSearchName]);

    const getDateRangeValues = () => {
        const { startDate, endDate } = dateRangeForm.getValues();
        return startDate && endDate ? { startDate, endDate } : undefined;
    };

    const generateLog = () => {
        dateRangeForm.trigger().then((valid) => {
            if (valid) {
                onGenerateLog?.(getDateRangeValues());
            }
        });
    };

    const onSearch = () => {
        dateRangeForm.trigger().then((valid) => {
            if (valid) {
                runSavedSearch(getDateRangeValues());
            }
        });
    };

    return (
        <HStack
            padding="var(--arc-space-4)"
            borderRadius="6px"
            justifyContent="space-between"
            _hover={{
                backgroundColor: 'var(--arc-colors-interactive-hover)',
            }}
            onClick={onSearch}
        >
            <VStack align="left" w="50%">
                {isEditMode ? (
                    <Box w="350px">
                        <Input
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            isLoading={updateSavedSearchNameMutation.isLoading}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                                switch (e.key) {
                                    case 'Enter':
                                        if (newNameIsValid) {
                                            updateSavedSearchName(savedSearchId, newName);
                                        }
                                        break;
                                    case 'Escape':
                                        cancelEdit();
                                        break;
                                    default:
                                        break;
                                }
                            }}
                            autoFocus
                        />
                    </Box>
                ) : (
                    <Text variant="headingSm">{savedSearchName}</Text>
                )}
                <Box maxWidth="720px">
                    <Text>{formattedParams}</Text>
                </Box>
                <Text variant="caption" color="tertiary">
                    Last used: {formatMonthDayYearHoursMinutesSeconds(lastExecutedDateUtc)}
                </Text>
            </VStack>
            <HStack justifyContent={showDateRangeOverride ? 'space-between' : 'flex-end'} w="50%">
                {showDateRangeOverride && (
                    <HStack>
                        <FormDateInput
                            size="sm"
                            length="md"
                            control={dateRangeForm.control}
                            name="startDate"
                            label="From"
                            granularity="minute"
                            hasCalendar
                            rules={{ validate: validateForm }}
                        />
                        <FormDateInput
                            size="sm"
                            length="md"
                            control={dateRangeForm.control}
                            name="endDate"
                            label="To"
                            granularity="minute"
                            hasCalendar
                            rules={{ validate: validateForm }}
                        />
                    </HStack>
                )}
                <ButtonGroup>
                    {isEditMode ? (
                        <>
                            <IconButton
                                icon="Check"
                                aria-label="save new search name"
                                variant="ghost"
                                isDisabled={!newNameIsValid}
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    updateSavedSearchName(savedSearchId, newName);
                                }}
                            />
                            <IconButton
                                icon="CloseX"
                                aria-label="cancel editing search name"
                                variant="ghost"
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    cancelEdit();
                                }}
                            />
                        </>
                    ) : (
                        <>
                            {!!onGenerateLog && (
                                <IconButton
                                    icon="Report"
                                    aria-label={GENERATE_LOG_BUTTON_ARIA}
                                    variant="ghost"
                                    onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation();
                                        generateLog();
                                    }}
                                />
                            )}
                            {manageDepartmentSharedEventSearchesEnabled && (
                                <IconButton
                                    icon="Person"
                                    aria-label={SHARE_SEARCH_BUTTON_ARIA}
                                    variant="ghost"
                                    onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation();
                                        open({
                                            type: 'SHARE_SAVED_SEARCH_MODAL',
                                            props: {
                                                savedSearchId,
                                                savedSearchName,
                                                shareEventSearchForm,
                                                onSuccess: () => refetchSavedSearches(),
                                            },
                                        });
                                    }}
                                />
                            )}
                            <IconButton
                                icon="Edit"
                                aria-label="edit saved search name"
                                variant="ghost"
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    setIsEditMode(true);
                                }}
                            />
                            <IconButton
                                icon="TrashCan"
                                aria-label="delete saved search"
                                variant="ghost"
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    return modals.confirm({
                                        onConfirm: () =>
                                            deleteSavedSearch().then(() => refetchSavedSearches()),
                                        title: 'Delete Saved Search',
                                        body: 'Are you sure you want to delete this saved search?',
                                        confirmLabel: 'Delete',
                                    });
                                }}
                            />
                        </>
                    )}
                </ButtonGroup>
            </HStack>
        </HStack>
    );
}
